import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import journeyPhotoService from '../data/JourneyPhotoService';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';

const CarouselImage = ({ photo, shouldLoad, onNext, onPrev, onClose, onEdit }) => {
   const [imageSrc, setImageSrc] = useState(photo.base64PhotoScaled);
   const [loading, setLoading] = useState(false);
   const [loaded, setLoaded] = useState(false);

   useEffect(() => {
      if (shouldLoad && !loaded) {
         let mounted = true;
         setLoading(true);
         journeyPhotoService.getJourneyPhoto(photo.id)
            .then(data => {
               if (mounted && data.base64PhotoLarge) {
                  setImageSrc(data.base64PhotoLarge);
                  setLoaded(true);
               }
            })
            .catch(err => console.error(err))
            .finally(() => {
               if (mounted) setLoading(false);
            });
         return () => { mounted = false; };
      }
   }, [shouldLoad, loaded, photo.id]);

   return (
      <figure className='carousel-image' style={{ position: 'relative', height: '70vh', width: '100%', margin: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
         {loading && (
            <div style={{
               position: 'absolute',
               top: '50%',
               left: '50%',
               transform: 'translate(-50%, -50%)',
               zIndex: 1
            }}>
               <CircularProgress />
            </div>
         )}
         <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '100%', maxHeight: '100%' }}>
            {onClose && (
                <IconButton 
                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                    sx={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        color: 'white',
                        border: '2px solid white',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.5)' },
                        zIndex: 2
                    }}
                >
                    <CloseIcon />
                </IconButton>
            )}
            {onEdit && (
                 <Button 
                    variant="contained" 
                    startIcon={<EditIcon />} 
                    onClick={(e) => { e.stopPropagation(); onEdit(); }}
                    sx={{ 
                        position: 'absolute',
                        top: '10px',
                        left: '10px',
                        backgroundColor: 'rgba(0, 165, 222, 0.8)', 
                        color: 'white', 
                        '&:hover': { backgroundColor: '#008bbd' },
                        zIndex: 2,
                        textTransform: 'none'
                    }}
                 >
                     Edit Photo
                 </Button>
            )}
            {onPrev && (
                <IconButton 
                    onClick={(e) => { e.stopPropagation(); onPrev(); }}
                    sx={{
                        position: 'absolute',
                        left: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'white',
                        border: '2px solid white',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.5)' },
                        zIndex: 2
                    }}
                >
                    <ArrowBackIosNewIcon />
                </IconButton>
            )}
            {onNext && (
                <IconButton 
                    onClick={(e) => { e.stopPropagation(); onNext(); }}
                    sx={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'white',
                        border: '2px solid white',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.5)' },
                        zIndex: 2
                    }}
                >
                    <ArrowForwardIosIcon />
                </IconButton>
            )}
            <img
               style={{ 
                  maxHeight: 'calc(70vh - 4em)', 
                  maxWidth: '100%', 
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain', 
                  opacity: loading ? 0.5 : 1, 
                  transition: 'opacity 0.3s',
                  display: 'block'
               }}
               src={imageSrc}
               alt={photo.caption}
               loading="lazy"
            />
            {photo.caption ?
               <figcaption style={{
                  marginTop: '0.5em', 
                  width: '100%', // Match the container width which is driven by image
                  border: '2px solid white', 
                  borderRadius: '10px', 
                  padding: '8px', 
                  color: 'white',
                  boxSizing: 'border-box',
                  overflowWrap: 'break-word'
               }}>
                  {photo.caption}
               </figcaption> 
            : ''}
         </div>
      </figure>
   );
};

CarouselImage.propTypes = {
   photo: PropTypes.object.isRequired,
   shouldLoad: PropTypes.bool,
   onNext: PropTypes.func,
   onPrev: PropTypes.func,
   onClose: PropTypes.func,
   onEdit: PropTypes.func
};

export default CarouselImage;